import React from 'react';
import { BrowserRouter, Navigate, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import Header from './components/Header';
import Footer from './components/Footer';
import CookieConsentBanner from './components/CookieConsentBanner';
import ExitIntentBar from './components/ExitIntentBar';
import RunningStackDashboardModal from './components/RunningStackDashboardModal';
import ScrollToTop from './components/ScrollToTop';
import Homepage from './pages/Homepage';
import CategoryPage from './pages/CategoryPage';
import ToolDetailPage from './pages/ToolDetailPage';
import SearchPage from './pages/SearchPage';
import ComparisonPage from './pages/ComparisonPage';
import FindPage from './pages/FindPage';
import StackSharePage from './pages/StackSharePage';
import StackDashboardPage from './pages/StackDashboardPage';
import TransparencyPage from './pages/TransparencyPage';
import AlternativesPage from './pages/AlternativesPage';
import ConsultingPage from './pages/ConsultingPage';
import NewsletterPage from './pages/NewsletterPage';
import BlogPage from './pages/BlogPage';
import BlogDetailPage from './pages/BlogDetailPage';
import BestPage from './pages/BestPage';
import BestUseCasePage from './pages/BestUseCasePage';
import AIStackPrescriptionsPage from './pages/AIStackPrescriptionsPage';
import ReviewsPage from './pages/ReviewsPage';
import CategoriesPage from './pages/CategoriesPage';
import FAQPage from './pages/FAQPage';
import AboutPage from './pages/AboutPage';
import CompanyPage from './pages/CompanyPage';
import ContactPage from './pages/ContactPage';
import SubmitToolPage from './pages/SubmitToolPage';
import DisclaimerPage from './pages/DisclaimerPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import SitemapPage from './pages/SitemapPage';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import MarketingPage from './pages/MarketingPage';
import AdminConsultingPage from './pages/AdminConsultingPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminSalesManagementPage from './pages/AdminSalesManagementPage';
import AdminCommandCenterPage from './pages/AdminCommandCenterPage';
import ClientLoginPage from './pages/ClientLoginPage';
import GuidesPage from './pages/GuidesPage';
import GuideDetailPage from './pages/GuideDetailPage';
import ResourcePage from './pages/ResourcePage';
import OrchestratorLabPage from './pages/OrchestratorLabPage';
import PublicGoRedirectPage from './pages/PublicGoRedirectPage';
import { useAuth } from './contexts/AuthContext';
import { useTheme } from './contexts/ThemeContext';
import '@/App.css';

const PublicLayout = ({ children }) => (
  <>
    <Header />
    {children}
    <ExitIntentBar />
    <RunningStackDashboardModal />
    <Footer />
    <CookieConsentBanner />
  </>
);

const ClientOrchestratorRoute = () => {
  const { loading, isAuthenticated, user } = useAuth();

  if (loading) {
    return null;
  }

  const isClientUser = isAuthenticated && (user?.role || '').toLowerCase() === 'user';
  const hasClientId = Boolean(user?.id);

  if (!isClientUser || !hasClientId) {
    return <Navigate to="/consulting#start" replace />;
  }

  return <PublicLayout><OrchestratorLabPage /></PublicLayout>;
};

const ThemeRouteSync = () => {
  const location = useLocation();
  const { syncThemeForPath } = useTheme();

  React.useEffect(() => {
    syncThemeForPath(location.pathname);
  }, [location.pathname, syncThemeForPath]);

  return null;
};

const RoutedShell = () => {
  const { adminTheme } = useTheme();
  const withAdminShell = (element) => (
    <div className="admin-theme-shell" data-admin-theme={adminTheme}>
      {element}
    </div>
  );

  return (
    <div className="App" style={{ backgroundColor: 'var(--bg-base)', color: 'var(--text-primary)', minHeight: '100vh' }}>
      <Routes>
        <Route path="/" element={<PublicLayout><Homepage /></PublicLayout>} />
        <Route path="/categories" element={<PublicLayout><CategoriesPage /></PublicLayout>} />
        <Route path="/categories/:slug" element={<PublicLayout><CategoryPage /></PublicLayout>} />
        <Route path="/tools/:slug" element={<PublicLayout><ToolDetailPage /></PublicLayout>} />
        <Route path="/search" element={<PublicLayout><SearchPage /></PublicLayout>} />
        <Route path="/compare" element={<PublicLayout><ComparisonPage /></PublicLayout>} />
        <Route path="/find" element={<PublicLayout><FindPage /></PublicLayout>} />
        <Route path="/my-stack/build" element={<PublicLayout><FindPage /></PublicLayout>} />
        <Route path="/dashboard/stack" element={<PublicLayout><StackDashboardPage /></PublicLayout>} />
        <Route path="/stacks/:slug" element={<PublicLayout><StackSharePage /></PublicLayout>} />
        <Route path="/transparency" element={<PublicLayout><TransparencyPage /></PublicLayout>} />
        <Route path="/consulting" element={<PublicLayout><ConsultingPage /></PublicLayout>} />
        <Route path="/alternatives/:slug" element={<PublicLayout><AlternativesPage /></PublicLayout>} />
        <Route path="/newsletter" element={<PublicLayout><NewsletterPage /></PublicLayout>} />
        <Route path="/login" element={<PublicLayout><ClientLoginPage /></PublicLayout>} />
        <Route path="/blog" element={<PublicLayout><BlogPage /></PublicLayout>} />
        <Route path="/blog/:slug" element={<PublicLayout><BlogDetailPage /></PublicLayout>} />
        <Route path="/guides" element={<PublicLayout><GuidesPage /></PublicLayout>} />
        <Route path="/guides/:slug" element={<PublicLayout><GuideDetailPage /></PublicLayout>} />
        <Route path="/resources/:slug" element={<PublicLayout><ResourcePage /></PublicLayout>} />
        <Route path="/best" element={<PublicLayout><BestPage /></PublicLayout>} />
        <Route path="/top-picks" element={<PublicLayout><BestPage /></PublicLayout>} />
        <Route path="/best/:slug" element={<PublicLayout><BestUseCasePage /></PublicLayout>} />
        <Route path="/ai-stacks" element={<PublicLayout><AIStackPrescriptionsPage /></PublicLayout>} />
        <Route path="/reviews" element={<PublicLayout><ReviewsPage /></PublicLayout>} />
        <Route path="/faq" element={<PublicLayout><FAQPage /></PublicLayout>} />
        <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
        <Route path="/about-us" element={<PublicLayout><AboutPage /></PublicLayout>} />
        <Route path="/company" element={<PublicLayout><CompanyPage /></PublicLayout>} />
        <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
        <Route path="/submit-tool" element={<PublicLayout><SubmitToolPage /></PublicLayout>} />
        <Route path="/disclaimer" element={<PublicLayout><DisclaimerPage /></PublicLayout>} />
        <Route path="/privacy-policy" element={<PublicLayout><PrivacyPolicyPage /></PublicLayout>} />
        <Route path="/privacy" element={<PublicLayout><PrivacyPolicyPage /></PublicLayout>} />
        <Route path="/terms-of-service" element={<PublicLayout><TermsOfServicePage /></PublicLayout>} />
        <Route path="/terms" element={<PublicLayout><TermsOfServicePage /></PublicLayout>} />
        <Route path="/sitemap" element={<PublicLayout><SitemapPage /></PublicLayout>} />
        <Route path="/my-ai-flow" element={<ClientOrchestratorRoute />} />
        <Route path="/orchestrator-lab" element={<ClientOrchestratorRoute />} />
        <Route path="/go/:slug" element={<PublicGoRedirectPage />} />

        <Route path="/admin" element={withAdminShell(<AdminLogin />)} />
        <Route path="/admin/dashboard" element={withAdminShell(<ProtectedRoute><AdminDashboard /></ProtectedRoute>)} />
        <Route path="/admin/consulting" element={withAdminShell(<ProtectedRoute><AdminConsultingPage /></ProtectedRoute>)} />
        <Route path="/admin/users" element={withAdminShell(<ProtectedRoute><AdminUsersPage /></ProtectedRoute>)} />
        <Route path="/admin/sales" element={withAdminShell(<ProtectedRoute><AdminSalesManagementPage /></ProtectedRoute>)} />
        <Route path="/admin/command-center" element={withAdminShell(<ProtectedRoute><AdminCommandCenterPage /></ProtectedRoute>)} />
        <Route path="/marketing" element={withAdminShell(<ProtectedRoute><MarketingPage /></ProtectedRoute>)} />
      </Routes>
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <ThemeRouteSync />
            <ScrollToTop />
            <RoutedShell />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
